/**
 * NumberingConfigPanel — F12.D T12.D.11 + F12.I E2.T1 (segment-based).
 *
 * Zakładka „Numeracja" w ModuleSettingsPage. Konfiguruje auto-numerację per
 * kategorię modułu — wybór szablonu, lista segmentów, polityka resetu, flagi.
 *
 * F12.I E2.T1 minimum scope:
 *  - Per-kategoria: collapsed (toggle + preview) → expanded (szablony + lista
 *    segmentów + reset policy + flagi + start/padding + save).
 *  - 4 buttony szablonów quick-start (D9) — klik wypełnia segmenty kategorii.
 *  - Lista segmentów read-only z usuwaniem per segment (bez DnD i Add).
 *  - Live preview używając pure `previewRecordNumber`.
 *  - 4 reset policies, 2 flagi (lock/override), startFrom + padding.
 *  - Save → PUT /api/modules/:id/categories/:typeId z V2 shape.
 *
 * Świadomie odłożone do kolejnych sub-tasków F12.I:
 *  - Drag&drop reorder segmentów (E2.T4).
 *  - Add segment popover (9 typów) + per-segment form (E2.T4 + E2.T5).
 *  - TargetFieldPicker — pole docelowe modułu (E2.T2).
 *  - Reset counter button + modal (E3.T2).
 *  - Walidator client-side fieldSlug missing (E2.T7).
 *  - Debounce 500ms na save (E2.T7).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { CheckCircle, ChevronDown, ChevronUp, Hash, ToggleLeft, ToggleRight } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  computeResetKey,
  DEFAULT_V2_CONFIG,
  type NumberingConfigV2,
  previewRecordNumber,
  QUICK_START_TEMPLATES,
  type ResetPolicy,
} from '@/lib/numbering-preview';
import { useTenantTimeZone } from '@/lib/settings/time-zone-api';

import { SegmentEditor } from './numbering/SegmentEditor';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Category {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  isActive: boolean;
  isDefault: boolean;
  /** Backend GET zwraca zawsze V2 (po normalize on read). */
  numberingConfig: NumberingConfigV2 | null;
}

interface ModuleField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
}

/**
 * Typy pól które mogą przyjąć wygenerowany numer (string typu "FV/2026/0001").
 * Wykluczone:
 *  - number/decimal/currency — typ NUMERIC nie przyjmie stringa
 *  - autonumber — generuje swój własny numer (konflikt z numeracją kategorii)
 *  - richtext/markdown — rendered jako HTML, semantycznie nie pasują
 *  - multi-column (address/daterange/location) — rozwijane na sub-kolumny przez
 *    `packMultiColumnValue`, nie ma kolumny o nazwie sluga
 *  - select/multiselect/checkbox/radio/relation/user/country/file/image/date/datetime
 *    — typy specjalistyczne, nie obsługują wpisania numeru
 */
const TARGET_FIELD_TYPES = new Set([
  'text',
  'textarea',
  'email',
  'phone',
  'url',
  'code',
  'barcode',
]);

interface Props {
  moduleId: string;
}

// ─── Per-category config panel ────────────────────────────────────────────────

interface CategoryRowProps {
  rt: Category;
  moduleId: string;
  /** Pola modułu (do TargetFieldPicker). Pre-fetched raz w panelu dla wszystkich kategorii. */
  targetFieldCandidates: ModuleField[];
  /** Pełna lista pól modułu (do field_value / conditional_text segmentów). */
  allFields: ModuleField[];
}

function CategoryRow({ rt, moduleId, targetFieldCandidates, allFields }: CategoryRowProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  // Okres resetu i podgląd numeru liczone w strefie tenanta — jak na serwerze.
  const tenantTimeZone = useTenantTimeZone();

  const [expanded, setExpanded] = useState(false);
  const [cfg, setCfg] = useState<NumberingConfigV2>(rt.numberingConfig ?? DEFAULT_V2_CONFIG);
  /** Stan modala potwierdzenia resetu: 'all' / 'period' / null. */
  const [pendingReset, setPendingReset] = useState<'all' | 'period' | null>(null);

  // F12.I E2.T7: mutation wspiera dwa tryby — explicit (button Save, z toast'em)
  // i quiet (auto-save z debounce, bez toast'a żeby uniknąć spam'u przy DnD/edycji).
  const mutation = useMutation({
    mutationFn: (vars: { payload: NumberingConfigV2 | null; quiet?: boolean }) =>
      api
        .put(`/modules/${moduleId}/categories/${rt.id}`, { numberingConfig: vars.payload })
        .then(() => ({ quiet: vars.quiet ?? false })),
    onSuccess: (result) => {
      if (!result.quiet) toast.show({ variant: 'success', title: t('tenant.numbering.saved') });
      void qc.invalidateQueries({ queryKey: ['module', moduleId, 'categories'] });
    },
    onError: (err) => {
      const msg = isAxiosError(err)
        ? ((err.response?.data as { error?: string })?.error ?? err.message)
        : String(err);
      toast.show({ variant: 'error', title: msg });
    },
  });

  // F12.I E2.T7 (D14): auto-save z debounce 500ms — wzorzec z useListingPreferences.
  // SellCall save immediate, my dorzucamy debounce żeby uniknąć spam'u przy DnD
  // sortowaniu segmentów / szybkiej edycji wielu pól. Initial mount skipowany żeby
  // nie odpalać save'a przy ładowaniu danych z backendu (cfg = rt.numberingConfig).
  // Quiet mode (bez toast'a) — toast tylko gdy user kliknie explicit Save button.
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      mutation.mutate({ payload: cfg, quiet: true });
    }, 500);
    return () => clearTimeout(timer);
    // mutation.mutate jest stabilną referencją z TanStack — ESLint warn ignorable
    // (dodanie do deps powoduje cykliczne re-create timera przy każdym mutation state change)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  // F12.I E3.T2: reset endpoint POST /numbering/reset z body { resetKey? }.
  const resetMutation = useMutation({
    mutationFn: (resetKey: string | null) =>
      api.post(
        `/modules/${moduleId}/categories/${rt.id}/numbering/reset`,
        resetKey === null ? {} : { resetKey },
      ),
    onSuccess: (_data, resetKey) => {
      const messageKey =
        resetKey === null
          ? 'tenant.numbering.reset.successAll'
          : 'tenant.numbering.reset.successPeriod';
      toast.show({ variant: 'success', title: t(messageKey) });
      // Counter rows zostały skasowane — invalidate categories nie jest konieczne
      // (numberingConfig się nie zmienia), ale invalidujemy żeby UI był spójny
      // gdyby ktoś dodał info o liczbie wygenerowanych numerów w przyszłości.
      void qc.invalidateQueries({ queryKey: ['module', moduleId, 'categories'] });
    },
    onError: (err) => {
      const msg = isAxiosError(err)
        ? ((err.response?.data as { error?: string })?.error ?? err.message)
        : String(err);
      toast.show({ variant: 'error', title: msg });
    },
  });

  const handleConfirmReset = () => {
    if (pendingReset === 'all') resetMutation.mutate(null);
    else if (pendingReset === 'period')
      resetMutation.mutate(computeResetKey(cfg.resetPolicy, new Date(), tenantTimeZone));
    setPendingReset(null);
  };

  const currentPeriodKey = computeResetKey(cfg.resetPolicy, new Date(), tenantTimeZone);

  const toggleEnabled = () => {
    const next = { ...cfg, enabled: !cfg.enabled };
    setCfg(next);
    // Toggle pokazuje toast (user-explicit działanie) — quiet=false.
    mutation.mutate({ payload: next, quiet: false });
  };

  const handleSave = () => {
    mutation.mutate({ payload: cfg, quiet: false });
  };

  const applyTemplate = (templateKey: (typeof QUICK_START_TEMPLATES)[number]['key']) => {
    const tpl = QUICK_START_TEMPLATES.find((q) => q.key === templateKey);
    if (!tpl) return;
    setCfg((c) => ({
      ...c,
      segments: tpl.segments,
      resetPolicy: tpl.resetPolicy,
    }));
  };

  const preview =
    cfg.segments.length > 0 ? previewRecordNumber(cfg, {}, undefined, tenantTimeZone) : '—';

  return (
    <div className="rounded-lg border border-border-primary bg-bg-primary overflow-hidden">
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white text-sm font-bold"
          style={{ backgroundColor: rt.color }}
          aria-hidden="true"
        >
          {rt.name.slice(0, 1).toUpperCase()}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text-primary">{rt.name}</p>
          {cfg.enabled && <p className="truncate text-xs font-mono text-text-muted">{preview}</p>}
        </div>

        {/* Toggle */}
        <button
          type="button"
          onClick={toggleEnabled}
          disabled={mutation.isPending}
          aria-label={cfg.enabled ? t('tenant.numbering.disable') : t('tenant.numbering.enable')}
          className="shrink-0 text-text-muted transition-colors hover:text-text-primary disabled:opacity-50"
          data-testid={`numbering-toggle-${rt.slug}`}
        >
          {cfg.enabled ? (
            <ToggleRight className="h-6 w-6 text-success-500" />
          ) : (
            <ToggleLeft className="h-6 w-6" />
          )}
        </button>

        {/* Expand */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? t('common.collapse') : t('common.expand')}
          className="shrink-0 text-text-muted hover:text-text-primary"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Expanded config */}
      {expanded && (
        <div className="border-t border-border-primary px-4 py-4 space-y-5">
          {/* Target field picker */}
          <FormField
            label={t('tenant.numbering.targetField.title')}
            hint={
              cfg.targetFieldSlug
                ? t('tenant.numbering.targetField.customField')
                : t('tenant.numbering.targetField.fallback')
            }
          >
            {targetFieldCandidates.length === 0 ? (
              <p className="rounded-md border border-dashed border-border-primary px-3 py-2 text-xs text-text-muted">
                {t('tenant.numbering.targetField.noFields')}
              </p>
            ) : (
              <Dropdown
                value={cfg.targetFieldSlug ?? ''}
                onChange={(v) =>
                  setCfg((c) => ({
                    ...c,
                    targetFieldSlug: v === '' ? undefined : v,
                  }))
                }
                className="block w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary"
                data-testid={`numbering-targetField-${rt.slug}`}
                options={[
                  { value: '', label: t('tenant.numbering.targetField.default') },
                  ...targetFieldCandidates.map((f) => ({
                    value: f.slug,
                    label: `${f.name} (${f.slug})`,
                  })),
                ]}
              />
            )}
          </FormField>

          {/* Quick-start templates */}
          <FormField label={t('tenant.numbering.templates.title')}>
            <p className="mb-2 text-xs text-text-muted">{t('tenant.numbering.templates.hint')}</p>
            <div className="flex flex-wrap gap-2">
              {QUICK_START_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.key}
                  type="button"
                  onClick={() => applyTemplate(tpl.key)}
                  className="rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-xs font-mono text-text-primary transition-colors hover:bg-bg-tertiary"
                  data-testid={`numbering-template-${rt.slug}-${tpl.key}`}
                >
                  {tpl.label}
                </button>
              ))}
            </div>
          </FormField>

          {/* Segments editor — Add popover + edycja per segment + delete (E2.T4a) */}
          <FormField label={t('tenant.numbering.segments.title')}>
            <SegmentEditor
              segments={cfg.segments}
              onChange={(next) => setCfg((c) => ({ ...c, segments: next }))}
              fields={allFields}
              testIdPrefix={`numbering-${rt.slug}`}
            />
          </FormField>

          {/* Live preview */}
          <div className="flex items-center gap-2 rounded-md bg-bg-secondary px-3 py-2">
            <CheckCircle className="h-4 w-4 shrink-0 text-success-500" />
            <span className="text-sm text-text-secondary">
              {t('tenant.numbering.preview')}:{' '}
              <span className="font-mono font-medium text-text-primary">{preview}</span>
            </span>
          </div>

          {/* Reset policy */}
          <FormField label={t('tenant.numbering.resetPolicy.title')}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(['never', 'yearly', 'monthly', 'daily'] as ResetPolicy[]).map((policy) => (
                <label
                  key={policy}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm hover:bg-bg-tertiary"
                >
                  <input
                    type="radio"
                    name={`numbering-resetPolicy-${rt.slug}`}
                    value={policy}
                    checked={cfg.resetPolicy === policy}
                    onChange={() => setCfg((c) => ({ ...c, resetPolicy: policy }))}
                    className="h-4 w-4 accent-brand-500"
                    data-testid={`numbering-resetPolicy-${rt.slug}-${policy}`}
                  />
                  <span className="text-text-secondary">
                    {t(`tenant.numbering.resetPolicy.${policy}`)}
                  </span>
                </label>
              ))}
            </div>
          </FormField>

          {/* Start from + padding */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label={t('tenant.numbering.startFrom')}>
              <Input
                type="number"
                min={1}
                value={cfg.startFrom}
                onChange={(e) =>
                  setCfg((c) => ({
                    ...c,
                    startFrom: Math.max(1, parseInt(e.target.value, 10) || 1),
                  }))
                }
                data-testid={`numbering-startFrom-${rt.slug}`}
              />
            </FormField>

            <FormField
              label={t('tenant.numbering.padding')}
              hint={t('tenant.numbering.paddingHint')}
            >
              <Input
                type="number"
                min={1}
                max={10}
                value={cfg.padding}
                onChange={(e) =>
                  setCfg((c) => ({
                    ...c,
                    padding: Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 4)),
                  }))
                }
                data-testid={`numbering-padding-${rt.slug}`}
              />
            </FormField>
          </div>

          {/* Flagi lock/override */}
          <div className="space-y-3">
            <FormField label={t('tenant.numbering.lockAfterGeneration')}>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={cfg.lockAfterGeneration}
                  onChange={(e) => setCfg((c) => ({ ...c, lockAfterGeneration: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 rounded border-border-primary accent-brand-500"
                  data-testid={`numbering-lockAfterGeneration-${rt.slug}`}
                />
                <span className="text-xs text-text-muted">
                  {t('tenant.numbering.lockAfterGenerationHint')}
                </span>
              </label>
            </FormField>

            <FormField label={t('tenant.numbering.allowManualOverride')}>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={cfg.allowManualOverride}
                  onChange={(e) => setCfg((c) => ({ ...c, allowManualOverride: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 rounded border-border-primary accent-brand-500"
                  data-testid={`numbering-allowManualOverride-${rt.slug}`}
                />
                <span className="text-xs text-text-muted">
                  {t('tenant.numbering.allowManualOverrideHint')}
                </span>
              </label>
            </FormField>
          </div>

          {/* Reset licznika (E3.T2) */}
          <FormField
            label={t('tenant.numbering.reset.title')}
            hint={t('tenant.numbering.reset.hint')}
          >
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setPendingReset('all')}
                disabled={resetMutation.isPending}
                className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-sm text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
                data-testid={`numbering-reset-all-${rt.slug}`}
              >
                {t('tenant.numbering.reset.allButton')}
              </button>
              {cfg.resetPolicy !== 'never' && (
                <button
                  type="button"
                  onClick={() => setPendingReset('period')}
                  disabled={resetMutation.isPending}
                  className="rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary disabled:opacity-50"
                  data-testid={`numbering-reset-period-${rt.slug}`}
                >
                  {t('tenant.numbering.reset.periodButton', { period: currentPeriodKey })}
                </button>
              )}
            </div>
          </FormField>

          {/* Save */}
          <div className="flex justify-end pt-1">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={mutation.isPending}
              data-testid={`numbering-save-${rt.slug}`}
            >
              {mutation.isPending ? <Spinner className="h-4 w-4" /> : t('common.save')}
            </Button>
          </div>
        </div>
      )}

      {/* ConfirmDialog dla resetu — wspólny dla obu trybów (all / period) */}
      <ConfirmDialog
        open={pendingReset !== null}
        title={t('tenant.numbering.reset.confirmTitle')}
        message={
          pendingReset === 'all'
            ? t('tenant.numbering.reset.confirmAll', { category: rt.name })
            : t('tenant.numbering.reset.confirmPeriod', {
                category: rt.name,
                period: currentPeriodKey,
              })
        }
        confirmLabel={t('tenant.numbering.reset.confirmLabel')}
        variant="danger"
        onConfirm={handleConfirmReset}
        onClose={() => setPendingReset(null)}
      />
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function NumberingConfigPanel({ moduleId }: Props) {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['module', moduleId, 'categories'],
    queryFn: async () => {
      const res = await api.get<{ categories: Category[] }>(`/modules/${moduleId}/categories`);
      return res.data.categories;
    },
    enabled: !!moduleId,
  });

  // F12.I E2.T2: pola modułu dla TargetFieldPicker — pre-fetch raz, dzielone
  // między wszystkie kategorie. Filtrowanie text-like robimy tutaj raz.
  const fieldsQuery = useQuery({
    queryKey: ['module', moduleId, 'fields'],
    queryFn: async () => {
      const res = await api.get<{ fields: ModuleField[] }>(`/modules/${moduleId}/fields`);
      return res.data.fields;
    },
    enabled: !!moduleId,
  });

  const allFields = fieldsQuery.data ?? [];
  const targetFieldCandidates = allFields.filter((f) => TARGET_FIELD_TYPES.has(f.fieldType));

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const types = query.data ?? [];

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Hash className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.moduleSettings.tabs.numbering')}
          </h2>
          <p className="text-sm text-text-muted">{t('tenant.numbering.description')}</p>
        </div>
      </div>

      {/* No record types */}
      {types.length === 0 && (
        <div className="rounded-lg border border-dashed border-border-primary py-12 text-center">
          <Hash className="mx-auto mb-3 h-8 w-8 text-text-muted" />
          <p className="text-sm text-text-muted">{t('tenant.numbering.noTypes')}</p>
          <p className="mt-1 text-xs text-text-tertiary">{t('tenant.numbering.noTypesHint')}</p>
        </div>
      )}

      {/* Category list */}
      {types.length > 0 && (
        <div className="space-y-3">
          {types.map((rt) => (
            <CategoryRow
              key={rt.id}
              rt={rt}
              moduleId={moduleId}
              targetFieldCandidates={targetFieldCandidates}
              allFields={allFields}
            />
          ))}
        </div>
      )}
    </div>
  );
}
